import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatError, api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Gavel, ShoppingBag, Building2 } from "lucide-react";

export default function RegisterPage() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [accountType, setAccountType] = useState("buyer"); // buyer | seller
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    business_name: "",
    business_phone: "",
    business_city: "",
    business_state: "",
    business_license: "",
  });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (accountType === "seller" && !form.business_name.trim()) {
      setErr("Business name is required for sellers");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        email: form.email,
        password: form.password,
        name: form.name,
        is_seller: accountType === "seller",
      };
      if (accountType === "seller") {
        payload.business_name = form.business_name;
        payload.business_phone = form.business_phone;
        payload.business_city = form.business_city;
        payload.business_state = form.business_state;
        payload.business_license = form.business_license;
      }
      await api.post("/auth/register", payload);
      await refresh();
      nav(accountType === "seller" ? "/seller" : "/dashboard");
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const isSeller = accountType === "seller";

  return (
    <div className="min-h-[80vh] grid lg:grid-cols-2">
      <div className="flex items-center justify-center p-6 sm:p-12 order-2 lg:order-1">
        <div className="w-full max-w-lg py-10">
          <Link to="/" className="inline-flex items-center gap-2 mb-8">
            <div className="w-8 h-8 bg-orange-600 flex items-center justify-center border border-orange-500">
              <Gavel className="w-4 h-4 text-white" />
            </div>
            <span className="font-display font-black text-lg tracking-tighter uppercase text-white">
              Storage<span className="text-orange-500">Vault</span>
            </span>
          </Link>

          <div className="text-[10px] tracking-[0.3em] uppercase text-orange-500 mb-2">Create Account</div>
          <h1 className="font-display text-4xl font-black uppercase tracking-tighter mb-8">
            {isSeller ? "List your first unit." : "Join the auction floor."}
          </h1>

          {/* Account type toggle */}
          <div className="mb-8">
            <Label className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-3 block">I want to…</Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                data-testid="account-type-buyer"
                onClick={() => setAccountType("buyer")}
                className={`p-4 border text-left transition-colors ${
                  !isSeller
                    ? "border-orange-500 bg-orange-500/10 text-white"
                    : "border-zinc-800 bg-zinc-950 text-zinc-500 hover:border-zinc-700"
                }`}
              >
                <ShoppingBag className={`w-5 h-5 mb-2 ${!isSeller ? "text-orange-500" : ""}`} />
                <div className="font-display text-lg font-bold uppercase tracking-tight">Bid</div>
                <div className="text-xs mt-1">Hunt for treasure in abandoned units.</div>
              </button>
              <button
                type="button"
                data-testid="account-type-seller"
                onClick={() => setAccountType("seller")}
                className={`p-4 border text-left transition-colors ${
                  isSeller
                    ? "border-orange-500 bg-orange-500/10 text-white"
                    : "border-zinc-800 bg-zinc-950 text-zinc-500 hover:border-zinc-700"
                }`}
              >
                <Building2 className={`w-5 h-5 mb-2 ${isSeller ? "text-orange-500" : ""}`} />
                <div className="font-display text-lg font-bold uppercase tracking-tight">Sell</div>
                <div className="text-xs mt-1">List units from my storage facility.</div>
              </button>
            </div>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Your Name</Label>
              <Input data-testid="register-name" required value={form.name} onChange={update("name")} className={inputCls} />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Email</Label>
              <Input data-testid="register-email" type="email" required value={form.email} onChange={update("email")} className={inputCls} />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Password (min 6)</Label>
              <Input data-testid="register-password" type="password" required minLength={6} value={form.password} onChange={update("password")} className={inputCls} />
            </div>

            {isSeller && (
              <div className="pt-4 mt-2 border-t border-zinc-800 space-y-4" data-testid="seller-fields">
                <div className="text-[10px] tracking-[0.3em] uppercase text-orange-500">Business Info</div>
                <div>
                  <Label className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Business / Facility Name</Label>
                  <Input data-testid="register-business-name" required={isSeller} value={form.business_name} onChange={update("business_name")} placeholder="e.g., Sunset Self Storage" className={inputCls} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">City</Label>
                    <Input data-testid="register-business-city" value={form.business_city} onChange={update("business_city")} className={inputCls} />
                  </div>
                  <div>
                    <Label className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">State</Label>
                    <Input data-testid="register-business-state" maxLength={2} value={form.business_state} onChange={update("business_state")} className={inputCls + " uppercase"} />
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Phone</Label>
                  <Input data-testid="register-business-phone" type="tel" value={form.business_phone} onChange={update("business_phone")} className={inputCls} />
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Business License / Lien-Holder ID (optional)</Label>
                  <Input data-testid="register-business-license" value={form.business_license} onChange={update("business_license")} className={inputCls} />
                </div>
                <p className="text-xs text-zinc-500 leading-relaxed">
                  By signing up as a seller, you confirm you have legal right to auction defaulted unit contents under your state&apos;s lien-holder statutes.
                </p>
              </div>
            )}

            {err && <div data-testid="register-error" className="text-red-500 text-sm">{err}</div>}
            <Button
              type="submit"
              data-testid="register-submit"
              disabled={loading}
              className="w-full rounded-none bg-orange-600 hover:bg-orange-500 border border-orange-500 h-12 text-sm uppercase tracking-widest font-semibold"
            >
              {loading ? "Creating…" : isSeller ? "Create Seller Account" : "Create Bidder Account"}
            </Button>
          </form>

          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-zinc-800" />
            <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-600">or</span>
            <div className="flex-1 h-px bg-zinc-800" />
          </div>

          <Button
            variant="outline"
            onClick={googleLogin}
            data-testid="google-register-button"
            className="w-full rounded-none bg-white hover:bg-zinc-100 text-zinc-900 border-zinc-300 h-12 text-sm uppercase tracking-widest font-semibold"
          >
            Continue with Google
          </Button>

          <p className="mt-8 text-sm text-zinc-500 text-center">
            Already have an account?{" "}
            <Link to="/login" data-testid="login-link" className="text-orange-500 hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </div>
      <div className="hidden lg:block relative border-l border-zinc-900 order-1 lg:order-2">
        <img
          src={isSeller
            ? "https://images.unsplash.com/photo-1685607498150-77db9bbafa31?w=1600&auto=format&fit=crop&q=80"
            : "https://images.unsplash.com/photo-1694601618351-dbbbb2b8934f?w=1600&auto=format&fit=crop&q=80"}
          alt=""
          className="w-full h-full object-cover opacity-40 transition-opacity"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-black via-black/60 to-transparent" />
        <div className="absolute bottom-12 right-12 left-12 lg:left-auto max-w-md text-right">
          <div className="text-[10px] tracking-[0.3em] uppercase text-orange-500 mb-2">
            {isSeller ? "For Facilities" : "For Bidders"}
          </div>
          <h2 className="font-display text-4xl font-black uppercase tracking-tighter leading-none text-white">
            {isSeller
              ? <>Recover lien value.<br /><span className="text-orange-500">Zero listing fees.</span></>
              : <>One account.<br /><span className="text-orange-500">Bid or sell.</span></>}
          </h2>
        </div>
      </div>
    </div>
  );
}

const inputCls = "rounded-none bg-zinc-950 border-zinc-800 focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-0 industrial-input h-12";
